import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import type { QuestionGroupTemplateContract } from '@/contracts/question-groups'
import { useAuth } from '@/lib/auth'
import { listQuestionGroupTemplates } from '@/lib/data-client/question-groups'
import { hasFormAdminAccess } from '@/lib/permissions'
import { formatRelativeTime } from '@/lib/utils'
import { FileQuestion, FolderTree, Layers3, LockKeyhole, Plus, Search, Tags } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { QuestionGroupEditorDialog } from './QuestionGroupEditorDialog'
import { QuestionGroupFormDialog } from './QuestionGroupFormDialog'

export function QuestionGroupLibraryPage() {
  const { user } = useAuth()
  const isAdmin = hasFormAdminAccess(user)
  const [keyword, setKeyword] = useState('')
  const [templates, setTemplates] = useState<QuestionGroupTemplateContract[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<QuestionGroupTemplateContract | null>(null)

  useEffect(() => {
    if (!isAdmin) return
    void refreshTemplates()
  }, [isAdmin])

  const filteredTemplates = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLocaleLowerCase()
    if (!normalizedKeyword) return templates
    return templates.filter((template) =>
      [template.title, template.description ?? '', template.category ?? '', template.tags.join(' ')]
        .join(' ')
        .toLocaleLowerCase()
        .includes(normalizedKeyword),
    )
  }, [keyword, templates])

  async function refreshTemplates() {
    setLoading(true)
    setLoadError('')
    try {
      setTemplates(await listQuestionGroupTemplates())
    } catch (error) {
      console.error('[MiaoForm] list question group templates failed', error)
      setLoadError('目前無法讀取題組庫，請稍後再試。')
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }

  function handleCreated(template: QuestionGroupTemplateContract) {
    void refreshTemplates()
    setEditingTemplate(template)
  }

  function handleSaved(template: QuestionGroupTemplateContract) {
    setTemplates((current) => current.map((item) => (item.id === template.id ? template : item)))
    void refreshTemplates()
  }

  if (!isAdmin) {
    return (
      <div className="grid min-h-[calc(100dvh-120px)] place-items-center p-6">
        <Card className="w-full max-w-xl">
          <CardContent className="p-8 text-center">
            <div className="mx-auto grid size-12 place-items-center rounded-full bg-slate-100 text-slate-500">
              <LockKeyhole className="size-6" />
            </div>
            <h1 className="mt-4 text-lg font-semibold text-slate-950">沒有題組管理權限</h1>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              題組庫會影響後續問卷引用與同步題組維護，目前僅管理員可以進入。請聯絡管理員調整 Email
              權限。
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-slate-50">
      <div className="shrink-0 border-b border-slate-200 bg-white p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-indigo-700">
              <FolderTree className="size-4" />
              題組管理
            </div>
            <h1 className="mt-2 text-2xl font-semibold text-slate-950">題組庫</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              問卷引用時可選「同步題組」或「從題組建立複本」。同步題組需回到題組管理編輯；複本只影響單一問卷。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              新增題組
            </Button>
          </div>
        </div>
        <div className="relative mt-5 max-w-lg">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜尋題組標題、分類或標籤"
            className="pl-9"
          />
        </div>
      </div>

      <ScrollArea className="min-h-0 flex-1" viewportClassName="p-6">
        {loading ? (
          <div className="grid min-h-72 place-items-center rounded-lg border border-dashed border-slate-300 bg-white text-center">
            <p className="text-sm text-slate-500">正在讀取題組庫...</p>
          </div>
        ) : loadError ? (
          <div className="grid min-h-72 place-items-center rounded-lg border border-rose-200 bg-rose-50 text-center">
            <p className="text-sm text-rose-700">{loadError}</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="grid min-h-72 place-items-center rounded-lg border border-dashed border-slate-300 bg-white text-center">
            <div>
              <FileQuestion className="mx-auto size-10 text-slate-300" />
              <h2 className="mt-4 font-semibold text-slate-950">找不到符合條件的題組</h2>
              <p className="mt-2 text-sm text-slate-500">請調整搜尋字詞，或建立新的題組模板。</p>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {filteredTemplates.map((template) => (
              <QuestionGroupTemplateCard
                key={template.id}
                template={template}
                onManage={() => setEditingTemplate(template)}
              />
            ))}
          </div>
        )}
      </ScrollArea>

      <QuestionGroupFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={handleCreated}
      />
      <QuestionGroupEditorDialog
        template={editingTemplate}
        open={Boolean(editingTemplate)}
        onOpenChange={(open) => {
          if (!open) {
            setEditingTemplate(null)
            refreshTemplates()
          }
        }}
        onSaved={handleSaved}
      />
    </div>
  )
}

function QuestionGroupTemplateCard({
  template,
  onManage,
}: {
  template: QuestionGroupTemplateContract
  onManage: () => void
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-lg font-semibold text-slate-950">{template.title}</h2>
              <Badge variant="outline">{template.category ?? '未分類'}</Badge>
            </div>
            <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-500">
              {template.description || '尚未填寫題組描述。'}
            </p>
          </div>
          <Button type="button" variant="secondary" onClick={onManage}>
            問題管理
          </Button>
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {template.tags.length > 0 ? (
            template.tags.map((tag) => (
              <Badge key={tag} variant="secondary">
                <Tags className="size-3" />
                {tag}
              </Badge>
            ))
          ) : (
            <Badge variant="secondary">無標籤</Badge>
          )}
        </div>
        <div className="mt-5 grid gap-3 text-sm text-slate-600 sm:grid-cols-3">
          <MetricTile
            icon={<FileQuestion className="size-4" />}
            label="問題數"
            value={`${template.questions.length}`}
          />
          <MetricTile
            icon={<Layers3 className="size-4" />}
            label="使用次數"
            value={`${template.usageCount}`}
          />
          <MetricTile label="更新時間" value={formatRelativeTime(template.updatedAt)} />
        </div>
      </CardContent>
    </Card>
  )
}

function MetricTile({
  icon,
  label,
  value,
}: {
  icon?: ReactNode
  label: string
  value: string
}) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-semibold text-slate-950">{value}</div>
    </div>
  )
}
